import { Tabs as BaseTabs } from '@base-ui/react/tabs'
import React, { useState } from 'react'
import styled from 'styled-components'
import { accents, semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

export interface TabItem {
  key: string
  label: React.ReactNode
  // Omit it for a tab that only reports a selection and leaves the content to
  // the caller; no panel is rendered.
  children?: React.ReactNode
}

export interface TabsProps {
  items: readonly TabItem[]
  // The selected key. Omit it (and use `defaultActiveKey`) for an uncontrolled
  // group; the first item is selected when neither is given.
  activeKey?: string
  defaultActiveKey?: string
  // Fires only for a selection the user made.
  onChange?: (key: string) => void
  className?: string
  style?: React.CSSProperties
  'data-testid'?: string
}

// Tinted by `--tabs-accent` so a call site can recolor the active tab and its
// bar without restyling either.
const accent = `var(--tabs-accent, ${semantic.accent})`

const Root = styled(BaseTabs.Root)`
  display: flex;
  flex-direction: column;
  min-height: 0;
`

const List = styled(BaseTabs.List)`
  position: relative;
  display: flex;
  flex-shrink: 0;
  gap: ${spacing[6]}px;
  border-bottom: 1px solid ${semantic.border};
`

const Tab = styled(BaseTabs.Tab)`
  margin: 0;
  padding: ${spacing[3]}px 0;
  border: 0;
  background: none;
  font-family: ${typography.fontFamily};
  font-size: ${typography.text.sm.fontSize}px;
  line-height: ${typography.text.sm.lineHeight}px;
  font-weight: ${typography.weights.medium};
  color: ${semantic.fgMuted};
  white-space: nowrap;
  cursor: pointer;
  transition: color ${motion.duration.fast}ms ${motion.ease};

  &:hover,
  &[data-active] {
    color: ${accent};
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

const Bar = styled(BaseTabs.Indicator)`
  position: absolute;
  left: 0;
  bottom: -1px;
  width: var(--active-tab-width);
  height: 2px;
  translate: var(--active-tab-left);
  background: ${accent};
  transition:
    translate ${motion.duration.base}ms ${motion.ease},
    width ${motion.duration.base}ms ${motion.ease};
`

const Panel = styled(BaseTabs.Panel)`
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 0;
  padding-top: ${spacing[4]}px;

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }

  &[hidden] {
    display: none;
  }
`

// Renders its children from the first time `active` is true and keeps them
// mounted from then on, so a tab's state survives switching away and back
// (antd's behavior) while a tab nobody opened never mounts.
const MountOnce = ({
  active,
  children,
}: {
  active: boolean
  children: React.ReactNode
}): React.ReactElement | null => {
  const [opened, setOpened] = useState(active)
  if (active && !opened) setOpened(true)
  return opened || active ? <>{children}</> : null
}

export const Tabs = ({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  className,
  style,
  'data-testid': testId,
}: TabsProps): React.ReactElement => (
  <Root
    value={activeKey}
    // Base UI's own default is `0`, which matches no string key; it then falls
    // back to the first tab and reports that as `missing`. Say the key so the
    // fallback never runs.
    defaultValue={
      activeKey === undefined ? (defaultActiveKey ?? items[0]?.key) : undefined
    }
    // Base UI also reports its automatic selections here (`initial`,
    // `missing`, `disabled`), and may pass `null`; only a user's pick is an
    // antd-style change.
    onValueChange={(next, details) => {
      if (details.reason === 'none' && typeof next === 'string')
        onChange?.(next)
    }}
    className={className}
    style={style}
    data-testid={testId}
  >
    <List>
      {items.map((item) => (
        <Tab key={item.key} value={item.key}>
          {item.label}
        </Tab>
      ))}
      <Bar />
    </List>
    {items.map((item) =>
      item.children === undefined ? null : (
        <Panel
          key={item.key}
          value={item.key}
          keepMounted
          render={(props, state) => (
            <div {...props}>
              <MountOnce active={!state.hidden}>{item.children}</MountOnce>
            </div>
          )}
        />
      ),
    )}
  </Root>
)

Tabs.displayName = 'Tabs'
